/**
 * A small hand-rolled icon set.
 *
 * Deliberately not a dependency: we need about ten glyphs, and the design system bans emoji
 * (docs/DESIGN_SYSTEM.md §1). An icon library would cost more in bundle and install weight
 * than these paths cost in maintenance. Add glyphs here as needed; if this passes ~30,
 * reconsider and measure.
 *
 * All icons are 16px, 1.5 stroke, currentColor, and decorative by default — give the
 * parent element the accessible name.
 */

export type IconName =
  | 'dashboard'
  | 'ledger'
  | 'map'
  | 'property'
  | 'tenancy'
  | 'owners'
  | 'people'
  | 'tools'
  | 'maintenance'
  | 'automation'
  | 'invoice'
  | 'bank'
  | 'report'
  | 'settings'
  | 'check'
  | 'alert'
  | 'clock'
  | 'arrow-right'
  | 'search'
  | 'bell'
  | 'calendar'
  | 'chevron-down'
  | 'chevron-right'
  | 'close'
  | 'plus'
  | 'sun'
  | 'moon'
  | 'monitor'
  | 'columns'
  | 'bookmark'
  | 'user'
  | 'bed'
  | 'bath'
  | 'swap'
  | 'payout'
  | 'shield'
  | 'sign-out'
  | 'panel'
  | 'more'
  | 'image'
  | 'tag'
  | 'eye'
  | 'eye-off'
  | 'mail'
  | 'help'
  | 'keyboard';

const PATHS: Record<IconName, React.ReactNode> = {
  dashboard: (
    <>
      <rect x="2" y="2" width="5.5" height="5.5" rx="1" />
      <rect x="10.5" y="2" width="5.5" height="5.5" rx="1" />
      <rect x="2" y="10.5" width="5.5" height="5.5" rx="1" />
      <rect x="10.5" y="10.5" width="5.5" height="5.5" rx="1" />
    </>
  ),
  ledger: (
    <>
      <path d="M3 2.5h9a1.5 1.5 0 0 1 1.5 1.5v11.5H4.5A1.5 1.5 0 0 1 3 14z" />
      <path d="M6 6h5M6 9h5M6 12h3" />
    </>
  ),
  map: (
    <>
      <path d="m2.5 4 4.5-1.5 5 1.5 4-1.5v12L12 16l-5-1.5-4.5 1.5z" />
      <path d="M7 2.5v12M12 4v12" />
    </>
  ),
  property: (
    <>
      <path d="M2.5 7.5 9 2.5l6.5 5" />
      <path d="M4 7v8.5h10V7" />
      <path d="M7.5 15.5v-4h3v4" />
    </>
  ),
  // A key: a tenancy is the handing over of one. Deliberately nothing like `property`, which
  // is a roof — the two sit next to each other in the rail and must not read as a pair.
  tenancy: (
    <>
      <circle cx="5.5" cy="6" r="3" />
      <path d="m7.7 8.2 6.3 6.3" />
      <path d="m11.5 12 1.6-1.6M13.4 13.9 15 12.3" />
    </>
  ),
  // A briefcase: an owner is an ENTITY that holds things — a bank account, a GST number, a
  // trust ledger position — not a person. The glyph says "organisation", not "human".
  owners: (
    <>
      <rect x="2.5" y="5.5" width="13" height="9" rx="1.5" />
      <path d="M6.5 5.5V4a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1.5" />
      <path d="M2.5 9.5h13" />
    </>
  ),
  people: (
    <>
      <circle cx="7" cy="6" r="2.5" />
      <path d="M2.5 15c0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5" />
      <path d="M12 4.2a2.5 2.5 0 0 1 0 4.6M13.5 15c0-1.6-.5-3-1.4-4" />
    </>
  ),
  bank: (
    <>
      <path d="M2 7 9 3l7 4" />
      <path d="M4 8v5M7.5 8v5M10.5 8v5M14 8v5" />
      <path d="M2.5 15.5h13" />
    </>
  ),
  /*
   * A spanner. The Contractors rail slot, and deliberately not a hard hat or a house-with-a-wrench:
   * it sits four places from `property`, which is a building, and the two must not read as
   * variations on the same glyph at 20px.
   */
  tools: (
    <>
      <path
        d="M11.5 2.5a3.5 3.5 0 0 0-3.1 4.9L3.7 12.1a1.75 1.75 0 0 0 2.5 2.5l4.7-4.7a3.5 3.5 0 0 0 4.6-4.4l-2.1 2.1-2.3-.5-.5-2.3 2.1-2.1a3.5 3.5 0 0 0-1.2-.7z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  /*
   * A hammer, cocked at 45 degrees: the head a bar, the handle a centred shaft, drawn upright
   * and rotated as one. The maintenance QUEUE, as distinct from `tools`' spanner, which is the
   * contractor file — the rail shows both at 16px with the label truncated on a phone, and two
   * icons of the same silhouette two rows apart is a rail nobody can navigate by shape.
   *
   * The translate is not decoration: rotating about the canvas centre leaves the glyph high and
   * left of it, and this puts it back. No claw and no forked prongs — both are sub-pixel
   * features at this size and close into a blot.
   */
  maintenance: (
    <g transform="translate(1.72 1.43) rotate(-45 9 9)">
      <rect x="3" y="2.6" width="11.6" height="4" rx="1.3" />
      <path d="M7.4 6.6v7.4a1.4 1.4 0 0 0 2.8 0V6.6" />
    </g>
  ),
  // A bolt. The universal "this happens by itself" mark, and deliberately NOT a gear: a gear
  // reads as settings, which is where an automation is decidedly not filed.
  automation: <path d="M9.75 1.5 3.5 10h4l-1.25 6.5L12.5 8h-4l1.25-6.5z" />,
  // A sheet of paper with lines on it — an invoice, as distinct from `report`'s bar chart.
  invoice: (
    <>
      <path d="M4.5 1.5h7A1.5 1.5 0 0 1 13 3v11.2a.4.4 0 0 1-.6.35L10.5 13.5 8.6 14.6a.8.8 0 0 1-.8 0L6 13.5l-1.9 1.05a.4.4 0 0 1-.6-.35V3A1.5 1.5 0 0 1 4.5 1.5z" />
      <path d="M6 5h4M6 7.5h4M6 10h2.5" />
    </>
  ),
  report: (
    <>
      <path d="M4 2.5h6L14 6.5v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-12a1 1 0 0 1 1-1z" />
      <path d="M9.5 2.5v4h4" />
      <path d="M6 10.5h5M6 13h3" />
    </>
  ),
  /*
   * A banknote with an arrow leaving it — money going OUT of the trust account.
   *
   * Deliberately not an arrow alone and not a hand: it sits in the rail two places from `bank`,
   * which is a building, and the pair has to read as "the account" and "money leaving it" at 20px
   * without either one being a generic arrow that could mean navigation.
   */
  payout: (
    <>
      <rect x="2" y="3" width="11" height="7" rx="1.5" />
      <circle cx="7.5" cy="6.5" r="1.75" />
      <path d="M7 14.5h7.5M12.5 12.5l2 2-2 2" />
    </>
  ),
  // Sliders, not a cog. The previous version was a circle with eight radiating lines —
  // structurally identical to `sun`, so it read as a second theme control sitting directly
  // under the theme toggle. Whatever replaces this must not be radially symmetric.
  settings: (
    <>
      <path d="M2.5 5.5h7M13.5 5.5h2M2.5 12.5h2M8.5 12.5h7" />
      <circle cx="11.5" cy="5.5" r="2" />
      <circle cx="6.5" cy="12.5" r="2" />
    </>
  ),
  check: <path d="m3.5 9.5 3.5 3.5 7.5-8" />,
  alert: (
    <>
      <path d="M9 2.5 16.5 15.5h-15z" />
      <path d="M9 7v3.5M9 13h.01" />
    </>
  ),
  clock: (
    <>
      <circle cx="9" cy="9" r="6.5" />
      <path d="M9 5v4l2.5 2" />
    </>
  ),
  'arrow-right': <path d="M3.5 9h11M10 4.5 14.5 9 10 13.5" />,
  search: (
    <>
      <circle cx="8" cy="8" r="5" />
      <path d="m11.8 11.8 3.2 3.2" />
    </>
  ),
  mail: (
    <>
      <rect x="2" y="3.5" width="14" height="11" rx="1.5" />
      <path d="m2.5 4.5 6.5 5 6.5-5" />
    </>
  ),
  help: (
    <>
      <circle cx="9" cy="9" r="6.75" />
      <path d="M7.1 7.1a2 2 0 0 1 3.85.75c0 1.35-1.95 1.65-1.95 3" />
      <path d="M9 12.9h.01" />
    </>
  ),
  keyboard: (
    <>
      <rect x="1.75" y="4.25" width="14.5" height="9.5" rx="1.5" />
      <path d="M4.75 7.25h.01M7.25 7.25h.01M9.75 7.25h.01M12.25 7.25h.01M4.75 10.75h8.5" />
    </>
  ),
  bell: (
    <>
      <path d="M4.5 7.5a4.5 4.5 0 0 1 9 0c0 3 1 4.5 1 4.5h-11s1-1.5 1-4.5z" />
      <path d="M7.2 14.5a1.9 1.9 0 0 0 3.6 0" />
    </>
  ),
  calendar: (
    <>
      <rect x="2.5" y="3.5" width="13" height="12" rx="1.5" />
      <path d="M2.5 7h13M6 2v3M12 2v3" />
    </>
  ),
  'chevron-down': <path d="m4.5 7 4.5 4.5L13.5 7" />,
  'chevron-right': <path d="m7 4.5 4.5 4.5L7 13.5" />,
  close: <path d="m4.75 4.75 8.5 8.5M13.25 4.75l-8.5 8.5" />,
  plus: <path d="M9 3.5v11M3.5 9h11" />,
  sun: (
    <>
      <circle cx="9" cy="9" r="3.25" />
      <path d="M9 1.5v1.75M9 14.75v1.75M16.5 9h-1.75M3.25 9H1.5M14.3 3.7l-1.24 1.24M4.94 13.06 3.7 14.3M14.3 14.3l-1.24-1.24M4.94 4.94 3.7 3.7" />
    </>
  ),
  moon: <path d="M15 10.9A6.5 6.5 0 0 1 7.1 3a6.75 6.75 0 1 0 7.9 7.9z" />,
  monitor: (
    <>
      <rect x="2" y="3" width="14" height="9.5" rx="1.5" />
      <path d="M6.5 15.5h5M9 12.5v3" />
    </>
  ),
  columns: (
    <>
      <rect x="2.5" y="3" width="13" height="12" rx="1.5" />
      <path d="M7 3v12M11 3v12" />
    </>
  ),
  bookmark: <path d="M5 2.5h8a1 1 0 0 1 1 1v12L9 12.5 4 15.5v-12a1 1 0 0 1 1-1z" />,
  user: (
    <>
      <circle cx="9" cy="6" r="3" />
      <path d="M3.5 15.5a5.5 5.5 0 0 1 11 0" />
    </>
  ),
  // A pillow on a frame, seen from the side — the bedrooms count, next to `bath` in the
  // rooms column, so the pair has to read as "sleep" vs. "wash" at a glance, not two beds.
  bed: (
    <>
      <path d="M2 15v-4.5A1.5 1.5 0 0 1 3.5 9H15a1.5 1.5 0 0 1 1.5 1.5V15" />
      <path d="M2 12.5h14.5" />
      <rect x="3.5" y="6.5" width="4.5" height="3" rx="1" />
      <path d="M2 15v1M16.5 15v1" />
    </>
  ),
  // A tub with a faucet — deliberately not a shower head, which at 16px reads as a sun burst
  // and collides with `sun`.
  bath: (
    <>
      <path d="M2.5 9V4.5A1.5 1.5 0 0 1 4 3a1.5 1.5 0 0 1 1.5 1.5V9" />
      <path d="M2 9h13.5a1 1 0 0 1 1 1v.5a4.5 4.5 0 0 1-4.5 4.5H6.5A4.5 4.5 0 0 1 2 10.5z" />
      <path d="M4 15.5v1M13 15.5v1" />
    </>
  ),
  swap: (
    <>
      <path d="M3 6.5h9.5M10 4l2.5 2.5L10 9" />
      <path d="M15 11.5H5.5M8 9l-2.5 2.5L8 14" />
    </>
  ),
  // Support access and anything else that is about who may see what.
  shield: <path d="M9 2.5 3.5 4.75V9c0 3.2 2.3 5.6 5.5 6.5 3.2-.9 5.5-3.3 5.5-6.5V4.75L9 2.5Z" />,
  'sign-out': (
    <>
      <path d="M11 5V3.5a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V13" />
      <path d="M7.5 9h8M13 6.5 15.5 9 13 11.5" />
    </>
  ),
  // A rail, divided: the collapse/expand toggle. The near vertical line stands for the
  // icon-only lane that survives either way, so the glyph reads as "fold the rest of this
  // away" rather than a generic layout icon.
  panel: (
    <>
      <rect x="2.5" y="3.5" width="13" height="11" rx="1.5" />
      <path d="M7 3.5v11" />
    </>
  ),
  // Overflow: the phone bar's fifth tab. Filled rather than stroked — a 1.5px ring at this
  // radius reads as a smudge, not a dot.
  more: (
    <g fill="currentColor" stroke="none">
      <circle cx="4" cy="9" r="1.4" />
      <circle cx="9" cy="9" r="1.4" />
      <circle cx="14" cy="9" r="1.4" />
    </g>
  ),
  // A photograph: a frame, a sun, and a mountain crossing it — the property's Images tab,
  // deliberately unlike `property` (a roof) since the two sit side by side in the same nav.
  image: (
    <>
      <rect x="2" y="3" width="14" height="12" rx="1.5" />
      <circle cx="6.5" cy="7" r="1.4" />
      <path d="m3 13.5 4-4 2.5 2.5 2-2L15.5 14" />
    </>
  ),
  tag: (
    <>
      <path d="M2.5 8.8V3.5a1 1 0 0 1 1-1h5.3l6.6 6.6a1 1 0 0 1 0 1.4l-5.3 5.3a1 1 0 0 1-1.4 0z" />
      <circle cx="6" cy="6" r="1.2" />
    </>
  ),
  // Show and hide a password. `eye-off` is the same eye struck through, so the pair reads as one
  // control in two states rather than two unrelated glyphs.
  eye: (
    <>
      <path d="M1.75 9S4.5 3.75 9 3.75 16.25 9 16.25 9 13.5 14.25 9 14.25 1.75 9 1.75 9Z" />
      <circle cx="9" cy="9" r="2.25" />
    </>
  ),
  'eye-off': (
    <>
      <path d="M7.4 3.95A6.9 6.9 0 0 1 9 3.75C13.5 3.75 16.25 9 16.25 9a13 13 0 0 1-1.9 2.6M4.6 5.1C2.8 6.5 1.75 9 1.75 9S4.5 14.25 9 14.25a6.6 6.6 0 0 0 3.6-1.05" />
      <path d="M7.4 7.4a2.25 2.25 0 0 0 3.2 3.2M2.5 2.5l13 13" />
    </>
  ),
};

export interface IconProps extends React.SVGAttributes<SVGSVGElement> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 16, className, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
